import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import { t } from 'i18next'

dayjs.extend(isoWeek)
dayjs.extend(quarterOfYear)

// ============================================================================
// Types
// ============================================================================

export interface DateRange {
    from: Date
    to?: Date
}

export interface Preset {
    name: string
    label: string
}

/** Quick end-date preset: calculates endDate relative to startDate */
export interface EndDatePreset {
    name: string
    label: string
    getEndDate: (startDate: dayjs.Dayjs) => Date
}

// ============================================================================
// Default presets
// ============================================================================

// Labels are getters so they follow the active language at render time.
export const DEFAULT_PRESETS: Preset[] = [
    {
        name: 'today',
        get label() {
            return t('date-preset.today')
        },
    },
    {
        name: 'yesterday',
        get label() {
            return t('date-preset.yesterday')
        },
    },
    {
        name: 'last7',
        get label() {
            return t('date-preset.last7')
        },
    },
    {
        name: 'last14',
        get label() {
            return t('date-preset.last14')
        },
    },
    {
        name: 'last30',
        get label() {
            return t('date-preset.last30')
        },
    },
    {
        name: 'thisWeek',
        get label() {
            return t('date-preset.thisWeek')
        },
    },
    {
        name: 'lastWeek',
        get label() {
            return t('date-preset.lastWeek')
        },
    },
    {
        name: 'thisMonth',
        get label() {
            return t('date-preset.thisMonth')
        },
    },
    {
        name: 'lastMonth',
        get label() {
            return t('date-preset.lastMonth')
        },
    },
    {
        name: 'thisQuarter',
        get label() {
            return t('date-preset.thisQuarter')
        },
    },
    {
        name: 'lastQuarter',
        get label() {
            return t('date-preset.lastQuarter')
        },
    },
    {
        name: 'last3Months',
        get label() {
            return t('date-preset.last3Months')
        },
    },
    {
        name: 'last6Months',
        get label() {
            return t('date-preset.last6Months')
        },
    },
    {
        name: 'thisYear',
        get label() {
            return t('date-preset.thisYear')
        },
    },
    {
        name: 'lastYear',
        get label() {
            return t('date-preset.lastYear')
        },
    },
]

// ============================================================================
// Preset range calculation
// ============================================================================

/** Calculate preset date range using dayjs for reliable date math */
export function getPresetRange(presetName: string): DateRange {
    const now = dayjs()

    switch (presetName) {
        case 'today':
            return {
                from: now.startOf('day').toDate(),
                to: now.endOf('day').toDate(),
            }
        case 'yesterday': {
            const y = now.subtract(1, 'day')
            return {
                from: y.startOf('day').toDate(),
                to: y.endOf('day').toDate(),
            }
        }
        case 'last7':
            return {
                from: now.subtract(6, 'day').startOf('day').toDate(),
                to: now.endOf('day').toDate(),
            }
        case 'last14':
            return {
                from: now.subtract(13, 'day').startOf('day').toDate(),
                to: now.endOf('day').toDate(),
            }
        case 'last30':
            return {
                from: now.subtract(29, 'day').startOf('day').toDate(),
                to: now.endOf('day').toDate(),
            }
        case 'thisWeek':
            return {
                from: now.startOf('isoWeek' as any).toDate(), // Monday
                to: now.endOf('isoWeek' as any).toDate(),
            }
        case 'lastWeek': {
            const lastMon = now.subtract(1, 'week').startOf('isoWeek' as any)
            const lastSun = lastMon.endOf('isoWeek' as any)
            return { from: lastMon.toDate(), to: lastSun.toDate() }
        }
        case 'thisMonth':
            return {
                from: now.startOf('month').toDate(),
                to: now.endOf('month').toDate(),
            }
        case 'lastMonth': {
            const lm = now.subtract(1, 'month')
            return {
                from: lm.startOf('month').toDate(),
                to: lm.endOf('month').toDate(),
            }
        }
        case 'thisQuarter': {
            return {
                from: now.startOf('quarter' as any).toDate(),
                to: now.endOf('quarter' as any).toDate(),
            }
        }
        case 'lastQuarter': {
            const lq = now.subtract(1, 'quarter' as any)
            return {
                from: lq.startOf('quarter' as any).toDate(),
                to: lq.endOf('quarter' as any).toDate(),
            }
        }
        case 'last3Months':
            return {
                from: now.subtract(2, 'month').startOf('month').toDate(),
                to: now.endOf('month').toDate(),
            }
        case 'last6Months':
            return {
                from: now.subtract(5, 'month').startOf('month').toDate(),
                to: now.endOf('month').toDate(),
            }
        case 'thisYear':
            return {
                from: now.startOf('year').toDate(),
                to: now.endOf('year').toDate(),
            }
        case 'lastYear': {
            const ly = now.subtract(1, 'year')
            return {
                from: ly.startOf('year').toDate(),
                to: ly.endOf('year').toDate(),
            }
        }
        default:
            return {
                from: now.startOf('day').toDate(),
                to: now.endOf('day').toDate(),
            }
    }
}

/**
 * End-date presets ("+1 month", "+3 months"…) as DS DateRangePicker presets
 * anchored on the chosen start day (today when none is picked yet).
 */
export function endDatePresetsFrom(
    start: unknown,
    presets: EndDatePreset[]
): { label: string; range: () => { from: Date; to: Date } }[] {
    const s = start ? dayjs(start as string | Date) : dayjs()
    const from = (s.isValid() ? s : dayjs()).startOf('day')
    return presets.map((p) => ({
        label: p.label,
        range: () => ({ from: from.toDate(), to: p.getEndDate(from) }),
    }))
}
